import { jsx as _jsx } from "react/jsx-runtime";
import * as React from "react";
import { ErrorMessage } from "./FormHelper.styles";
export function useFormHelper(formModel) {
    return React.useMemo(() => new FormHelper(formModel), [formModel]);
}
export class FormHelper {
    constructor(formModel) {
        this.formModel = formModel;
        this.nullToBlank = (value) => (value === null ? "" : value);
    }
    bindAutocomplete(property) {
        return {
            value: this.formModel.input[property],
            onChange: (value) => {
                this.updateInput(property, value);
            },
            error: !!this.formModel.errors[property],
            required: this.formModel.requiredFields.includes(property),
            helperText: this.formModel.errors[property],
        };
    }
    bindAutocompleteMulti(property) {
        return {
            value: this.formModel.input[property],
            onChange: (value) => {
                this.updateInput(property, value);
            },
            error: !!this.formModel.errors[property],
            required: this.formModel.requiredFields.includes(property),
            helperText: this.formModel.errors[property],
        };
    }
    bindTextField(property) {
        return {
            value: this.nullToBlank(this.formModel.input[property]),
            onChange: (event) => {
                this.updateInput(property, event.target.value);
            },
            error: !!this.formModel.errors[property],
            required: this.formModel.requiredFields.includes(property),
            helperText: this.formModel.errors[property],
        };
    }
    bindTextFieldPositiveNumber(property) {
        return {
            type: "number",
            value: this.nullToBlank(this.formModel.input[property]),
            onChange: (event) => {
                if (event.target.value === "") {
                    this.updateInput(property, "");
                    return;
                }
                const valueAsInt = Math.max(0, parseInt(event.target.value));
                this.updateInput(property, String(valueAsInt));
            },
            error: !!this.formModel.errors[property],
            required: this.formModel.requiredFields.includes(property),
            helperText: this.formModel.errors[property],
        };
    }
    bindTextFieldArray(property, list, index) {
        return {
            value: this.formModel.input[property],
            onChange: (event) => {
                list[index] = event.target.value;
                this.updateInput(property, list);
            },
            error: !!this.formModel.errors[property],
            required: this.formModel.requiredFields.includes(property),
            helperText: this.formModel.errors[property],
        };
    }
    bindNumericField(property) {
        return {
            value: this.formModel.input[property] === null ? "" : this.formModel.input[property],
            onChange: (event) => {
                const value = event.target.value;
                if (value === "" || /^[0-9\b]+$/.test(value)) {
                    const numericValue = value === "" ? null : Number(value);
                    this.updateInput(property, numericValue);
                }
            },
            error: !!this.formModel.errors[property],
            required: this.formModel.requiredFields.includes(property),
            helperText: this.formModel.errors[property],
        };
    }
    bindRawCheckbox(property) {
        return {
            checked: this.formModel.input[property],
            onChange: (event) => {
                this.updateInput(property, event.target.checked);
            },
        };
    }
    bindCheckbox(property) {
        return {
            checked: this.formModel.input[property],
            onChange: (_event, checked) => {
                this.updateInput(property, checked);
            },
        };
    }
    bindCheckboxList(property, listItem, allChoices) {
        const list = this.formModel.input[property];
        return {
            checked: list.includes(listItem),
            onChange: (_event, checked) => {
                const set = new Set(list);
                if (checked) {
                    set.add(listItem);
                }
                else {
                    set.delete(listItem);
                }
                this.updateInput(property, allChoices.filter((choice) => set.has(choice)));
            },
        };
    }
    bindBooleanRadioGroup(property) {
        return {
            value: this.formModel.input[property],
            onChange: (_event, value) => {
                this.updateInput(property, value === "true");
            },
        };
    }
    bindRadioGroup(property) {
        return {
            value: this.formModel.input[property],
            onChange: (_event, value) => {
                this.updateInput(property, value);
            },
        };
    }
    bindSwitch(property) {
        var _a;
        return {
            checked: (_a = this.formModel.input[property]) !== null && _a !== void 0 ? _a : undefined,
            onChange: (_event, checked) => {
                this.updateInput(property, checked);
            },
        };
    }
    bindDatePicker(property) {
        return {
            value: typeof this.formModel.input[property] === "string" && this.formModel.input[property]
                ? new Date(this.formModel.input[property])
                : null,
            onChange: (date) => {
                this.updateInput(property, date ? date.toISOString() : null);
            },
        };
    }
    bindLabel(...properties) {
        return {
            required: properties.some((property) => this.formModel.requiredFields.includes(property)),
            error: properties.some((property) => !!this.formModel.errors[property]),
        };
    }
    hasError(property) {
        return !!this.formModel.errors[property];
    }
    errorMessage(property) {
        return this.formModel.errors[property] && _jsx(ErrorMessage, { children: this.formModel.errors[property] });
    }
    formErrorMessage() {
        return this.formModel.formError && _jsx(ErrorMessage, { children: this.formModel.formError });
    }
    updateInput(property, value) {
        this.formModel.updateInput({ [property]: value });
    }
}
